import { useSelector, useDispatch } from "react-redux";
import { devicesActions } from "../store";
import { useEffect, useRef } from "react";
import { map } from "./map";

let lastRun = new Date().getTime();
let first_time = true;
let old_data = {};
const animatingDevices = {}; // {deviceId: {animationId, startTime, startCoord, endCoord}}
const animation_duration = 5000; // 5 ثانیه

const LivePositions = () => {
    const devices = useSelector((state) => state.devices.items);
    const positions = Object.values(useSelector((state) => state.positions.items));
    const selectedId = useSelector((state) => state.devices.selectedId);

    // ذخیره آخرین موقعیت هر دستگاه
    const lastDevicePositions = useRef({});

    const dispatch = useDispatch();

    const createFeature = (position, devices, selectedId, is_many_devices) => {
        const device = devices[position.deviceId] || {};
        const lonlat = [position.longitude, position.latitude];
        let out = {};

        if (is_many_devices) {
            const color = { offline: "rgb(255, 100, 100)", online: "rgb(100, 255, 100)", unknown: "rgb(38, 60, 153)" };

            out = {
                id: position.deviceId,
                position: lonlat,
                name: is_many_devices ? "" : (device && device.name) || "خودرو پیدا نشد",
                priority: (device && (device.id === selectedId ? 1 : 0)) || 0,
                size: device.id === selectedId ? 11.25 : 7.5,
                color: device.id === selectedId ? "#ffcc12" : color[(device && device.status) || "unknown"],
            };
        } else {
            out = {
                id: position.deviceId,
                position: lonlat,
                name: `${is_many_devices ? "" : (device && device.name) || "خودرو پیدا نشد"} \n ${position.speed} Km/h`,
                icon: (device && device.status) || "unknown",
                priority: (device && (device.id === selectedId ? 1 : 0)) || 0,
                // size: device.id === selectedId ? 1.3 : 1,
                // iconSize: device.status === "online" ? 0.1 : 1,
                // offset: device.status === "online" ? [50, -50] : [0, 0],
                // rotation: position.course,
            };
        }

        return out;
    };

    // شروع انیمیشن برای یک دستگاه
    const startDeviceAnimation = (deviceId, startCoord, endCoord, devices, positions, selectedId, is_many_devices) => {
        // اگر در حال انیمیشن است، انیمیشن قبلی را متوقف کن
        if (animatingDevices[deviceId]) {
            cancelAnimationFrame(animatingDevices[deviceId].animationId);
        }

        // چک کردن فاصله - اگر فاصله خیلی کم است، انیمیشن نکن
        const distance = Math.sqrt(Math.pow(endCoord[0] - startCoord[0], 2) + Math.pow(endCoord[1] - startCoord[1], 2));

        if (distance < 0.00001) {
            // حدود 1 متر
            // فاصله خیلی کم است، مستقیم به موقعیت نهایی برو
            const position = positions.find((p) => p.deviceId === deviceId);
            if (position) {
                old_data[deviceId] = createFeature(position, devices, selectedId, is_many_devices);
                old_data[deviceId].position = [...endCoord];
                renderAllDevices(devices, positions, selectedId, is_many_devices);
            }
            return;
        }

        const startTime = performance.now();

        function animate(time) {
            const elapsed = time - startTime;
            const t = Math.min(elapsed / animation_duration, 1);

            // اطمینان از اینکه t بین 0 و 1 باشد
            const clampedT = Math.max(0, Math.min(1, t));

            // استفاده از interpolation خطی برای سرعت یکنواخت
            const lon = startCoord[0] + (endCoord[0] - startCoord[0]) * clampedT;
            const lat = startCoord[1] + (endCoord[1] - startCoord[1]) * clampedT;

            // به‌روزرسانی موقعیت فعلی این دستگاه
            const position = positions.find((p) => p.deviceId === deviceId);
            if (position) {
                old_data[deviceId] = createFeature({ ...position, longitude: lon, latitude: lat }, devices, selectedId, is_many_devices);
                old_data[deviceId].position = [lon, lat];
            }

            // رندر کل نقشه
            renderAllDevices(devices, positions, selectedId, is_many_devices);

            if (t < 1) {
                animatingDevices[deviceId].animationId = requestAnimationFrame(animate);
            } else {
                // انیمیشن این دستگاه تمام شد
                if (position) {
                    old_data[deviceId] = createFeature(position, devices, selectedId, is_many_devices);
                    old_data[deviceId].position = [position.longitude, position.latitude];
                }
                delete animatingDevices[deviceId];
                renderAllDevices(devices, positions, selectedId, is_many_devices);
            }
        }

        animatingDevices[deviceId] = {
            animationId: requestAnimationFrame(animate),
            startTime,
            startCoord: [...startCoord],
            endCoord: [...endCoord],
        };
    };

    // رندر کردن تمام دستگاه‌ها
    const renderAllDevices = (devices, positions, selectedId, is_many_devices) => {
        const devicesSource = map.getSource("devices" + (is_many_devices ? "-many" : ""));
        if (!devicesSource) return;

        const features = positions.map((position) => {
            const properties = old_data[position.deviceId] || createFeature(position, devices, selectedId, is_many_devices);

            return {
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: properties.position || [position.longitude, position.latitude],
                },
                properties,
            };
        });

        devicesSource.setData({
            type: "FeatureCollection",
            features,
        });
    };

    const processPositions = (devices, positions, selectedId, is_many_devices) => {
        if (!positions.length) return;

        positions.forEach((position) => {
            const deviceId = position.deviceId;
            const newCoord = [position.longitude, position.latitude];
            const lastPos = lastDevicePositions.current[deviceId];

            // چک کردن تغییر موقعیت این دستگاه
            const hasChanged = !lastPos || lastPos.longitude !== position.longitude || lastPos.latitude !== position.latitude;

            if (hasChanged) {
                // ذخیره موقعیت جدید
                lastDevicePositions.current[deviceId] = {
                    longitude: position.longitude,
                    latitude: position.latitude,
                    course: position.course,
                    speed: position.speed,
                };

                const oldFeature = old_data[deviceId];
                const startCoord = oldFeature ? oldFeature.position : newCoord;

                // فقط اگر واقعاً جابجا شده باشد، انیمیشن شروع کن
                if (!oldFeature || startCoord[0] !== newCoord[0] || startCoord[1] !== newCoord[1]) {
                    startDeviceAnimation(deviceId, startCoord, newCoord, devices, positions, selectedId, is_many_devices);
                }
            } else {
                // موقعیت تغییر نکرده، اگر در حال انیمیشن نیست، فقط properties را به‌روزرسانی کن
                if (!animatingDevices[deviceId]) {
                    old_data[deviceId] = createFeature(position, devices, selectedId, is_many_devices);
                    if (old_data[deviceId] && !old_data[deviceId].position) {
                        old_data[deviceId].position = newCoord;
                    }
                }
            }
        });

        // اگر هیچ انیمیشنی در حال اجرا نیست، یک بار رندر کن
        if (Object.keys(animatingDevices).length === 0) {
            renderAllDevices(devices, positions, selectedId, is_many_devices);
        }
    };

    useEffect(() => {
        const devices_length = Object.keys(devices).length;
        if (devices_length !== 0) {
            const is_many_devices = devices_length > 10;

            if (first_time) {
                if (!positions.length) return;
                if (positions.length === 1) {
                    dispatch(devicesActions.select(positions[0].deviceId));
                    map.flyTo({ center: [positions[0].longitude, positions[0].latitude], zoom: 14 });
                }
                first_time = false;

                processPositions(devices, positions, selectedId, is_many_devices);
            }

            if (new Date().getTime() - lastRun > (is_many_devices ? 2000 : 600)) {
                processPositions(devices, positions, selectedId, is_many_devices);
                lastRun = new Date().getTime();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [devices, positions, selectedId]);

    useEffect(() => {
        const devices_length = Object.keys(devices).length;
        if (devices_length !== 0) {
            const is_many_devices = devices_length > 10;
            // فقط رندر دوباره کن، انیمیشن‌های در حال اجرا را قطع نکن
            renderAllDevices(devices, positions, selectedId, is_many_devices);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    return [];
};

export default LivePositions;
